import { Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentAccessWeekdays } from 'modules/core/hooks/useCurrentAccessWeekdays'
import i18n from 'modules/core/lib/i18n'

export interface Props {
  value: number[]
  onChange: (value: number[]) => void
  readonly?: boolean
  canUnselectAll?: boolean
}

const getWorkDaysLabel = (type: string) => {
  switch (type) {
    case 'friday':
      return i18n.t('project_management.selectors.week_days.days.friday')
    case 'monday':
      return i18n.t('project_management.selectors.week_days.days.monday')
    case 'saturday':
      return i18n.t('project_management.selectors.week_days.days.saturday')
    case 'sunday':
      return i18n.t('project_management.selectors.week_days.days.sunday')
    case 'thursday':
      return i18n.t('project_management.selectors.week_days.days.thursday')
    case 'tuesday':
      return i18n.t('project_management.selectors.week_days.days.tuesday')
    case 'wednesday':
      return i18n.t('project_management.selectors.week_days.days.wednesday')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

const getWorkDaysIndex = (type: string) => {
  switch (type) {
    case 'monday':
      return 1
    case 'tuesday':
      return 2
    case 'wednesday':
      return 3
    case 'thursday':
      return 4
    case 'friday':
      return 5
    case 'saturday':
      return 6
    case 'sunday':
      return 0
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

const WeekDaysSelector = ({ value, onChange, readonly, canUnselectAll }: Props) => {
  const weekDays = useCurrentAccessWeekdays()

  const select = (day: number) => {
    if (readonly) return

    const index = value.findIndex((d: number) => d === day)

    if (index > -1) {
      if (!canUnselectAll && value.length === 1) return
      return onChange(value.filter((d: number) => d !== day))
    }

    return onChange(value.concat([day]))
  }

  return (
    <Box
      padding="s16"
      gap="s8"
      background="grey100"
      borderRadius={{ all: 'abs016' }}
      border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
    >
      <Text.Normal size="200" weight="medium" color="grey800">
        {i18n.t('project_management.selectors.week_days.label')}:
      </Text.Normal>
      <Box flexDirection="row" gap="s8">
        {weekDays.map((day) => {
          const shortDay = getWorkDaysLabel(day ?? '')
          const isSelected = value.includes(getWorkDaysIndex(day))

          return (
            <Box
              minWidth="s24"
              minHeight="s24"
              textAlign="center"
              background={isSelected ? 'viridian1000' : 'viridian100'}
              borderRadius={{ all: 'abs004' }}
              onClick={() => select(getWorkDaysIndex(day))}
              key={day}
            >
              <Text.Normal color={isSelected ? 'white' : 'grey800'}>{shortDay}</Text.Normal>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default observer(WeekDaysSelector)
